import { useForm } from 'react-hook-form';
import {
  useAuthenticateUser,
  useSendOTP,
} from '../../../services/outer_layout/auth';
import { Link } from 'react-router-dom';
import { Button, Input } from '@unified-checkout/design-system';
import {
  ClickErrorLoginModal,
  ClickOTPModal,
  ClickPassKeySetupModal,
  ClickPhoneVerificationModal,
  OneClickPhoneInput,
  LoadingModal,
  getTermsAndConditionsURL
} from '@unified-checkout/kit-components';
import {
  IAuthenticateUserPostRequestPayload,
} from 'apps/customer/src/types';
import { Envelope } from '@unified-checkout/design-system/icons/index';
import { useEffect, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { getUserData } from 'apps/customer/src/utils';
import {
  useResendOtp,
} from 'apps/customer/src/services/inner_layout/auth';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getDomainCountry } from 'libs/kit-components/src/utils/getDomainCountry';

export const Login = () => {
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, isValid },
    getValues,
    resetField
  } = useForm<IAuthenticateUserPostRequestPayload>({
    mode: 'onBlur',
    shouldUnregister: true
  });

  const userProfile = getUserData()
  const { phone, email, isEmailVerified, customerId, currentPhase, isPhoneVerified } = userProfile;

  const [domainCountry, setDomainCountry] = useState(getDomainCountry);
  const { t } = useTranslation('flash', { keyPrefix: 'payments' });  

  const {
    mutateAsync: authMutationAsync,
    isPending: isPendingAuth,
    isPendingResendOtp,
  } = useAuthenticateUser({
    onPasskeysFailed: (event) => {
      if (event === 'OTP_REQUIRED' || event === 'PASSKEY_FAILED') {
        setOTPModalType('phone');
      }
    },
  });

  const { mutateAsync: sendOTPMutation, error: sendOTPError, reset: resetSendOTP, isPending: isPendingSendOTP } = useSendOTP();
  const { mutateAsync: resendOtpMutationAsync } = useResendOtp();

  const [isPassKeyModalOpen, setIsPassKeyModalOpen] = useState(false);
  const [isPhoneVerificationModalOpen, setIsPhoneVerificationModalOpen] =
    useState(false);
  const [isErrorLoginModalOpen, setIsErrorLoginModalOpen] = useState(false);
  const [showPhoneInput, setShowPhoneInput] = useState(false);

  const [OTPModalType, setOTPModalType] = useState<'email' | 'phone'>();
  const [OTPError, setOTPError] = useState('');
  const otpErrorFromSendOTP = getOtpErrorMessage(sendOTPError);
  const otpErrorFromResendOTPRef = useRef('');
  const [isResendingOTP, setIsResendingOTP] = useState(false);

  useEffect(() => {
    const resendAndOpenModal = async () => {
      if (currentPhase === 'LOGIN_VERIFICATION_OTP' && (!isPendingResendOtp && !isPendingAuth)) {
        setIsResendingOTP(true);
          await handleResendOTP({ sendTo: 'phone' });
          if (!otpErrorFromResendOTPRef.current) {
            setOTPModalType('phone');
          }
        setIsResendingOTP(false);
      }
    };

    resendAndOpenModal();
  }, [currentPhase]);

  const handleAuthentication = async (
    data: IAuthenticateUserPostRequestPayload
  ) => {
    const email = getValues('email');
    const phone = getValues('phone_number');
    try {
      const res = await authMutationAsync({
        email,
        ...(phone && { phone_number: domainCountry.code + phone }),
        ...(domainCountry.iso && { country: domainCountry.iso }),
      });
      if (
        res.action === 'login_otp' ||
        res.action === 'signup' ||
        res.action === 'activate'
      )
        setOTPModalType('phone');
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.status === 404) {
        setShowPhoneInput(true);
      }
      if (error instanceof AxiosError && error.status === 429) {
        toast.error('Too many attempts. Please try again after sometime.');
      }
      if(error instanceof AxiosError && error.status === 400) {
        const errorMessage = (error.response?.data as { message: string }).message;
        const normalizedErrorMessage = errorMessage?.toLowerCase();
        const shouldResetRegisterFlow = normalizedErrorMessage?.includes('error while sending otp');
        if(shouldResetRegisterFlow) {
          setShowPhoneInput(false);
        }
      }
    }
  };

  const emailRegistration = () =>
    register('email', {
      onChange: (e) => {
        const value = e.target.value;
        setValue('email', value);
        setShowPhoneInput(false);
        resetField('phone_number');
        clearErrors('phone_number');
      },
      validate: (value) => {
        if (!value) return 'Email is required';
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        return emailRegex.test(value) || 'Enter a valid email address';
      },
    });

  const submitOTPHandler = async (isValid: boolean, otp: string) => {
    if (!isValid) return;
    setOTPError('');
    const payload = {
      customer_id: customerId!,
      ...(OTPModalType === 'email' ? { email_otp: otp } : { phone_otp: otp }),
    };
    try {
     return await sendOTPMutation(payload);
    } catch(error) {
      return error as any;
    }
  };

  const handleResendOTP = async ({ sendTo, phone }: any) => {
    let payload: any = {
      customer_id: customerId,
      sent_to: sendTo,
    };
    setOTPError('');
    otpErrorFromResendOTPRef.current = '';
    resetSendOTP();
    try {
      if (isPhoneVerificationModalOpen || OTPModalType === 'email') {
        if (phone) {
          setValue('phone_number', phone);
        }
        if (!isEmailVerified) {
          payload.phone_number = phone
            ? domainCountry.code + phone
            : domainCountry.code + getValues('phone_number');
          payload.country = domainCountry.iso;
        }
      }
      const res = await resendOtpMutationAsync(payload);
      if (res.customer_id) {
        isPhoneVerificationModalOpen && setIsPhoneVerificationModalOpen(false);
        if (sendTo === 'email') {
          setOTPModalType('email');
        }
      }
    } catch (error) {
      if ((error as AxiosError).response?.status !== 400 && (error as AxiosError).response?.status !== 429) {
        toast.error(
          ((error as AxiosError).response?.data as { message: string })
            .message || 'Failed to resend OTP'
        );
      }
      if ((error as AxiosError).response?.status === 429 && !isPhoneVerificationModalOpen) {
        toast.error('Too many attempts . Please try after 1 min.');
      }
      setOTPError(
        ((error as AxiosError).response?.data as { message: string }).message ||
          'Failed to resend OTP'
      );
      otpErrorFromResendOTPRef.current = 'ERROR';
    }
  };

  const handleSendToEmail = async () => {
    if (isEmailVerified) {
      await handleResendOTP({ sendTo: 'email' });
      setOTPModalType('email');
    }
    if (!isEmailVerified) {
      setOTPModalType(undefined);
      setIsPhoneVerificationModalOpen(true);
    }
  };

  const resetStateAfterRegistration = () => {
    setOTPModalType(undefined);
    setIsPassKeyModalOpen(false);
    setIsPhoneVerificationModalOpen(false);
    setShowPhoneInput(false);
    resetField('phone_number');
    resetSendOTP();
  };

  const cancelOTPHandler = () => {
    resetStateAfterRegistration();
  };
  const submitPassKeyHandler = () => {
    setIsPassKeyModalOpen(false);
  };

  const cancelPassKeyModalHandler = () => {
    setIsPassKeyModalOpen(false);
  };

  const cancelPhoneHandler = () => {
    setOTPError('');
    resetStateAfterRegistration();
  };

  const retryLoginHandler = () => {
    setIsErrorLoginModalOpen(false);
  };

  function getOtpErrorMessage(error: unknown): string {
    if (!axios.isAxiosError(error)) return '';

    switch (error.response?.status) {
      case 429:
        return 'Too many attempts. Please try after 1 min.';
      case 423:
        return 'Too many incorrect attempts. Please request a new OTP to continue';
      default:
        return '';
    }
  };
  const TermsAndConditionsURL = getTermsAndConditionsURL();

  return (
    <div className="w-full px-5 pt-5 pb-8 md:px-0">
      <LoadingModal show={isResendingOTP} text={t('LOADING_MODAL_MESSAGE')} />
      <div className="pb-5">
        <div className="flex flex-col items-start gap-2 pb-5">
          <label
            htmlFor="emailOrPhone"
            className="english-body-kantumruyPro-normal text-[#000]"
          >
            Sign Up/Login information
          </label>
          <div className="w-full">
            <Input
              leadingIcon={Envelope}
              placeholder="mail@example.com"
              {...emailRegistration()}
              status={errors.email?.message ? 'error' : 'default'}
            />
            {showPhoneInput && (
              <OneClickPhoneInput
                clearErrors={clearErrors}
                register={register}
                setValue={setValue}
                errors={{}}
                country={domainCountry}
                setCountry={()=>{}}
                disableDropdown
              />
            )}
          </div>

          {errors.email && (
            <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
          )}
          {errors.phone_number && (
            <p className="mt-1 text-sm text-red-500">
              {errors.phone_number.message}
            </p>
          )}
        </div>

        <div className="[&_button]:w-full">
          <Button
            color="primary"
            text={isPendingAuth || isPendingResendOtp ? '' : "Continue"}
            type="submit"
            size="md"
            disabled={isPendingAuth || isPendingResendOtp || !isValid}
            onClickHandler={handleSubmit(handleAuthentication)}
            loading={isPendingAuth || isPendingResendOtp}
          />
        </div>
      </div>
      {OTPModalType && (
        <ClickOTPModal
          isOpen={!!OTPModalType}
          onCancel={cancelOTPHandler}
          onSubmit={submitOTPHandler}
          onResend={handleResendOTP}
          type={OTPModalType}
          onSendToEmail={handleSendToEmail}
          email={email}
          phone={phone}
          error={otpErrorFromSendOTP}
          isTopLayer={false}
          isPhoneVerified={!!isPhoneVerified}
        />
      )}
      {isPassKeyModalOpen && (
        <ClickPassKeySetupModal
          isOpen={isPassKeyModalOpen}
          onSubmit={submitPassKeyHandler}
          onCancel={cancelPassKeyModalHandler}
        />
      )}
      {isPhoneVerificationModalOpen && (
        <ClickPhoneVerificationModal
          isOpen={isPhoneVerificationModalOpen}
          onVerify={handleResendOTP}
          onCancel={cancelPhoneHandler}
          phoneHint={phone}
          OTPError={OTPError}
          country={domainCountry}
          setCountry={setDomainCountry}
        />
      )}
      {isErrorLoginModalOpen && (
        <ClickErrorLoginModal
          isOpen={isErrorLoginModalOpen}
          retryHandler={retryLoginHandler}
        />
      )}
      <p className="english-body-kantumruyPro-normal text-[#333]">
        By creating an account, you agree to our{' '}
        <a
          href={TermsAndConditionsURL}
          target="_blank"
          className="english-body-kantumruyPro-medium text-[#144DFF] underline"
        >
          Terms and Conditions.
        </a>
      </p>
    </div>
  );
};
